import { saveAs } from 'file-saver';
import { App as AntdApp } from 'antd';
import { useCallback } from 'react';
import {UseLazyQuery, UseMutation} from "@reduxjs/toolkit/dist/query/react/buildHooks";
import {BaseQueryFn, MutationDefinition, QueryDefinition} from "@reduxjs/toolkit/query";
import {FetchArgs, FetchBaseQueryError} from "@reduxjs/toolkit/query/react";
import { TagType } from '../../../store/services/api/base-api';
import {ReportResponse} from "../../../store/services/api/report-api";
import {convertBase64toBlob} from "../../../utils/helpers";

interface UseReportProps<TParams> {
  query: UseLazyQuery<QueryDefinition<TParams, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, TagType, ReportResponse, "api">> |
    UseMutation<MutationDefinition<TParams, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, TagType, ReportResponse, "api">>;
}

export const useReport = <TParams,>({query}: UseReportProps<TParams>) => {
  const { notification: apiNotification } = AntdApp.useApp();
  const [getReport, status] = query();

  const handleError = useCallback(() => {
    apiNotification.error({
      message: 'Error',
      description: 'There was an issue downloading the report.',
      placement: 'bottomRight',
      duration: 3,
    });
  }, [apiNotification]);

  const downloadReport = useCallback(
    async (params: TParams, contentType: string = 'application/pdf') => {
      try {
        const data = await getReport(params).unwrap();
        if (!data) {
          handleError();
          return;
        }

        let blob = convertBase64toBlob(data.reportData, contentType);
        saveAs(blob, data.fileName);
      } catch {
        handleError();
      }
    },
    [query, handleError, getReport]
  );

  return {
    downloadReport,
    isLoading: status?.isLoading || status?.isFetching,
  };
};
